// /reports/pnl — Profit & Loss (PAGES.md §3.43)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { PnlLine } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Row = PnlLine & { id: string };

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    { key: 'category', header: 'Category', render: (_v, row) => <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.category}</span> },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      render: (_v, row) => {
        const cls = row.amount < 0 ? 'text-danger-600' : 'text-neutral-900 dark:text-neutral-100';
        // Signed, as the CSV export writes it: a loss on Net Profit must not read as a profit
        // (QA 8.4.5). `|| 0` keeps an empty deduction from printing as -0.00.
        return <span className={`text-sm font-semibold tabular-nums ${cls}`}>{formatPaise(row.amount || 0, currency)}</span>;
      },
    },
  ];
}

export function ProfitLossReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'pnl',
    filter,
    queryFn: (f) => reportsApi.getPnl(f),
  });

  const rows: Row[] = useMemo(
    () => (data?.lines ?? []).map((l, i) => ({ ...l, id: `${l.category}-${i}` })),
    [data],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `pnl-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Category', get: (r: Row) => r.category },
          { header: `Amount (${symbol})`, get: (r: Row) => (r.amount / subunit).toFixed(csvDecimals) },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Profit & Loss', `Revenue: ${formatPaise(data?.revenue ?? 0, currency)} • Net Profit: ${formatPaise(data?.net_profit ?? 0, currency)} • Margin: ${(data?.margin_pct ?? 0).toFixed(1)}%`);
    }
  };

  const profitTone = (data?.net_profit ?? 0) >= 0 ? 'success' : 'danger';

  return (
    <ReportPageShell
      title="Profit & Loss"
      subtitle="Revenue, COGS, operating expenses and net profit with margin calculation."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Profit & Loss' }]}
      accent="teal-success"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'revenue', title: 'Revenue', value: formatPaise(data?.revenue ?? 0, currency), tone: 'brand', icon: KpiIcons.rupee },
              { id: 'cogs', title: 'COGS', value: formatPaise(data?.cogs ?? 0, currency), tone: 'warning', icon: KpiIcons.cart },
              { id: 'gross', title: 'Gross Profit', value: formatPaise(data?.gross_profit ?? 0, currency), tone: 'teal', icon: KpiIcons.trendUp, subLabel: `Margin ${(data?.margin_pct ?? 0).toFixed(1)}%` },
              { id: 'net', title: 'Net Profit', value: formatPaise(data?.net_profit ?? 0, currency), tone: profitTone, icon: (data?.net_profit ?? 0) >= 0 ? KpiIcons.trendUp : KpiIcons.trendDown, subLabel: `Margin ${(data?.net_margin_pct ?? 0).toFixed(1)}%` },
            ]}
          />
          <ReportTable
            title="Line Items"
            columns={columns}
            data={rows}
            emptyTitle="No financial data for selected period"
            totalCount={rows.length}
          />
        </>
      )}
    </ReportPageShell>
  );
}
